import { useMemo } from "react";
import { TaskInstance } from "@/lib/api/checklist";
import { SendAchFormCore, type AchFormData } from "@/section/ach/components/SendAchFormCore";

interface SendAchFormProps {
  taskInstance: TaskInstance;
  onSuccess?: () => void;
}

export function SendAchForm({ taskInstance, onSuccess }: SendAchFormProps) {
  // Get trigger meta data for prefilling
  const triggerMetaData = taskInstance.template_instance?.trigger_meta_data || {};
  const mailingCountry = triggerMetaData.mailing_country as string | undefined;
  const achFormCountryType = triggerMetaData.ach_form_country_type as "USA" | "CAD" | undefined;

  // Derive country code: first from ach_form_country_type, then from mailing address
  const derivedCountryType: "USA" | "CAD" = useMemo(() => {
    if (achFormCountryType === "USA" || achFormCountryType === "CAD") {
      return achFormCountryType;
    } else if (mailingCountry) {
      return /canada|cad/i.test(mailingCountry) ? "CAD" : /usa/i.test(mailingCountry) ? "USA" : "USA";
    }
    return "USA";
  }, [achFormCountryType, mailingCountry]);

  // Transform task instance data to initial form data
  const initialData: Partial<AchFormData> = useMemo(() => {
    return {
      customer_name: (triggerMetaData.customer_name as string) || "",
      customer_email: (triggerMetaData.email as string) || "",
      customer_phone_number: (triggerMetaData.customer_phone_number as string) || (triggerMetaData.phone_number as string) || "",
      lender_email: (triggerMetaData.banker_email as string) || "",
      lender_name: (triggerMetaData.banker_name as string) || "",
      lender_institution: (triggerMetaData.bank_institution_name as string) || "",
      lender_phone_number: (triggerMetaData.banker_phone as string) || "",
      lender_title: (triggerMetaData.banker_title as string) || "",
      has_lienholder: triggerMetaData.has_lienholder ?? false,
      ach_form_country_type: derivedCountryType,
    };
  }, [triggerMetaData, derivedCountryType]);

  return (
    <SendAchFormCore
      initialData={initialData}
      taskInstanceId={taskInstance.id}
      allowEditLienholder={false}
      allowEditCountryType={false}
      onSuccess={onSuccess}
    />
  );
}
